import { useContext, useEffect, useMemo, useRef } from "react";
import { WorkspaceContext } from "src/context/workspace";
import { classNames } from "src/helpers/classNames";
import { addClickPrevention, addSecurityTags, fixIframeUrls, fixInvalidLinks, prepareEmbeddedHtml } from "src/helpers/pageHelper";
import useDevice from "src/hooks/useDevice";

export default function CustomHtmlEmbed({ content }) {
  const { details: workspaceDetails } = useContext(WorkspaceContext);
  const iframeRef = useRef(null);
  const device = useDevice();

  const finalHtml = useMemo(() => {
    try {
      let rawHtml = content?.custom_html || "";

      if (!rawHtml) {
        return "";
      }

      let prepared = prepareEmbeddedHtml(rawHtml);

      const containsTailwind = /cdn\.tailwindcss\.com/i.test(prepared) || /tailwind(\.min)?\.css/i.test(prepared);
      const containsFontAwesome = /font-awesome/i.test(prepared) || /fontawesome/i.test(prepared);

      const tailwindCdn = `<script src="https://cdn.tailwindcss.com"></script>`;
      const fontAwesomeCdn = `<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />`;

      const isFullDoc = /<\s*html[\s>]/i.test(prepared);

      if (isFullDoc) {
        const injected = [!containsFontAwesome ? fontAwesomeCdn : "", !containsTailwind ? tailwindCdn : ""].filter(Boolean).join("\n  ");

        if (injected) {
          if (/(<\/head>)/i.test(prepared)) {
            prepared = prepared.replace(/(<\/head>)/i, () => `${injected}\n</head>`);
          } else if (/(<head[^>]*>)/i.test(prepared)) {
            prepared = prepared.replace(/(<head[^>]*>)/i, (match) => `${match}\n  ${injected}`);
          }
        }
      } else {
        prepared = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <base target="_blank" />
  ${!containsFontAwesome ? fontAwesomeCdn : ""}
  ${!containsTailwind ? tailwindCdn : ""}
  <style>
    html, body { margin: 0; padding: 0; min-height: 100%; }
    img, video { max-width: 100%; height: auto; }
    * { box-sizing: border-box; }
  </style>
</head>
<body>
${prepared}
</body>
</html>`;
      }

      prepared = addSecurityTags(prepared);
      prepared = fixInvalidLinks(prepared);
      prepared = fixIframeUrls(prepared);
      prepared = addClickPrevention(prepared);

      return prepared;
    } catch (error) {
      console.error("Error processing custom HTML:", error);
      return "";
    }
  }, [content]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !finalHtml) return;

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(finalHtml);
        doc.close();
      }
    } catch (e) {
      console.warn("Iframe write failed:", e);
    }
  }, [finalHtml]);

  if (!content?.custom_html) return null;

  return (
    <div className={classNames("relative flex h-full min-h-[400px] w-full flex-1 flex-col overflow-hidden")}>
      <div
        className="relative z-0 mx-auto flex h-full min-h-[400px] w-full flex-1 flex-col overflow-hidden"
        style={{ maxWidth: device === "desktop" ? workspaceDetails?.max_width || "100%" : "100%" }}>
        <iframe
          ref={iframeRef}
          id="iframe"
          title="Custom Html Embed"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
          style={{
            border: "none",
            margin: "0",
            padding: "0",
            width: "100%",
            height: "100%",
            display: "block",
            flex: "1 1 auto",
            backfaceVisibility: "hidden",
          }}
        />
      </div>
    </div>
  );
}
